import React, { useState } from "react";
import { X, Mail, Lock, User, LogIn, UserPlus, AlertCircle } from "lucide-react";

export default function AuthModal({ t, onClose, onLogin, onRegister }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setMessage("");
    setLoading(true);
    
    try {
      if (mode === "login") {
        await onLogin(email, password);
      } else {
        await onRegister(email, password, name);
      }
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-in fade-in duration-200">
      {/* Overlay cliquable */}
      <div 
        className="absolute inset-0" 
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        
        {/* Header avec gradient moderne */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-6 sm:p-8 text-white relative overflow-hidden">
          {/* Effet de brillance en arrière-plan */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl" />
          
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute -top-2 -right-2 p-2 hover:bg-white/10 rounded-full transition-all duration-300 group"
            >
              <X size={24} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </button>

            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center">
                {mode === "login" ? (
                  <LogIn size={24} strokeWidth={2.5} />
                ) : (
                  <UserPlus size={24} strokeWidth={2.5} />
                )}
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight">
                  {mode === "login" ? "Connexion" : "Inscription"}
                </h2>
              </div>
            </div>
            <div className="h-1 w-16 bg-red-600 rounded-full" />
          </div>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-8 space-y-5">
          
          {/* Message d'erreur */}
          {message && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-2xl animate-in slide-in-from-top duration-300">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-medium">{message}</p>
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-gray-700">
              <Mail size={16} className="text-red-600" />
              {t.email || "Email"}
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="w-full p-4 pl-12 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:border-red-600 focus:bg-white focus:outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400"
                autoComplete="email"
              />
              <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-gray-700">
              <Lock size={16} className="text-red-600" />
              {t.password || "Mot de passe"}
            </label>
            <div className="relative">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full p-4 pl-12 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:border-red-600 focus:bg-white focus:outline-none transition-all font-medium text-gray-900"
                autoComplete="current-password"
              />
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Name (uniquement pour inscription) */}
          {mode === "register" && (
            <div className="space-y-2 animate-in slide-in-from-top duration-300">
              <label className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-gray-700">
                <User size={16} className="text-red-600" />
                Nom complet
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="w-full p-4 pl-12 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:border-red-600 focus:bg-white focus:outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400"
                />
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          )}

          {/* Bouton Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-4 rounded-2xl font-black uppercase text-sm tracking-widest transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-3 touch-manipulation"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Chargement...</span>
              </>
            ) : (
              <>
                {mode === "login" ? (
                  <>
                    <LogIn size={20} />
                    {t.loginButton || "Se connecter"}
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    {t.registerButton || "Créer un compte"}
                  </>
                )}
              </>
            )}
          </button>

          {/* Toggle mode */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-gray-500 font-bold tracking-wider">Ou</span>
            </div>
          </div>

          <button
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setMessage("");
            }}
            className="w-full text-gray-700 hover:text-red-600 font-bold text-sm transition-colors py-3 rounded-xl hover:bg-gray-50 uppercase tracking-wide"
          >
            {mode === "login" ? (
              <span>Pas de compte ? <span className="text-red-600">Inscrivez-vous</span></span>
            ) : (
              <span>Déjà inscrit ? <span className="text-red-600">Connectez-vous</span></span>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <span className="font-bold uppercase tracking-wider">Connexion sécurisée</span>
          </div>
        </div>
      </div>
    </div>
  );
}